import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { Artifact } from '../../models/artifact.model';
import { ArtifactService } from '../../services/artifact.service';

/**
 * Compact in-chat representation of a report (icon + title + actions). Clicking
 * the card opens/toggles it in the report panel. The quick actions (open in new
 * tab / download) only appear when the artifact carries a fileUrl, and they
 * don't open the panel. Rendered wherever a report used to render inline.
 */
@Component({
  selector: 'app-artifact-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './artifact-card.component.html',
  styleUrls: ['./artifact-card.component.scss'],
})
export class ArtifactCardComponent {
  @Input() artifact!: Artifact;

  /** Emitted when the user clicks "Reanalyze" on a NEAT report card. The parent
   *  re-runs the analysis on the same transcript with force_reanalyze. */
  @Output() reanalyze = new EventEmitter<Artifact>();

  private readonly artifacts = inject(ArtifactService);

  get isActive(): boolean {
    return this.artifacts.panelOpen()
      && this.artifacts.activeArtifact()?.id === this.artifact.id;
  }

  get icon(): string {
    return this.artifact.icon || 'pi-file';
  }

  /** Show the Reanalyze action only for NEAT reports that know their source
   *  transcript file (so we can re-send it for a forced fresh pass). */
  get canReanalyze(): boolean {
    return this.artifact.agentId === 'NEAT_AGENT' && !!this.artifact.uploadFilename;
  }

  onReanalyze(event: Event): void {
    event.stopPropagation();
    this.reanalyze.emit(this.artifact);
  }

  onOpen(): void {
    this.artifacts.toggle(this.artifact);
  }

  onOpenNewTab(event: Event): void {
    event.stopPropagation();
    this.artifacts.openInNewTab(this.artifact);
  }

  onDownload(event: Event): void {
    event.stopPropagation();
    void this.artifacts.download(this.artifact);
  }
}
